import { Pencil, Plus, Trash2, Upload } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormSection } from '@components/form-section';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { toast } from '@core/toast';
import {
    ProductState,
    ProductSupportDto,
    ProductPriceDto
} from '@api/WalletClient';
import { getCdnImage } from '../../components/utils/cdn-image';
import { links } from '../../core/router';
import { injectTSDI } from '../../core/tsdi';
import { Api } from '../../core/api';
import { ImageRequirements } from '../components/image-requirements';
import { StateSummary } from '../components/state-summary';

interface ProductInfoSectionProps {
    product: ProductSupportDto;
    vendorId: number;
    productId: number;
    onToggleState: (newState: ProductState) => Promise<void>;
    loading: boolean;
    onImageUpdate: () => void;
    onPriceUpdate: () => void;
}

const InfoField = ({
    label,
    children
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <div>
        <span className="text-xs text-muted-foreground">{label}</span>
        <p className="text-sm">{children}</p>
    </div>
);

export const ProductInfoSection: React.FC<ProductInfoSectionProps> = ({
    product,
    vendorId,
    productId,
    onToggleState,
    loading,
    onImageUpdate,
    onPriceUpdate
}) => {
    const navigate = useNavigate();
    const { walletClient } = injectTSDI(Api);

    const [imageUploadLoading, setImageUploadLoading] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const [priceDialogOpen, setPriceDialogOpen] = useState(false);
    const [editingPrice, setEditingPrice] = useState<ProductPriceDto | null>(
        null
    );
    const [priceValue, setPriceValue] = useState('');
    const [priceCurrency, setPriceCurrency] = useState('');
    const [priceStock, setPriceStock] = useState('');
    const [priceLoading, setPriceLoading] = useState(false);
    const [priceError, setPriceError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingPrice, setDeletingPrice] = useState<ProductPriceDto | null>(
        null
    );

    const handleImageSelect = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }

        const maxSizeBytes = 5 * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            toast.error('File size must be less than 5MB');
            return;
        }

        setImageUploadLoading(true);

        try {
            await walletClient.supportApi.vendorSupportControllerReplaceVendorProductImage(
                vendorId,
                productId,
                { file }
            );

            onImageUpdate();
        } catch (error) {
            console.error('Image upload failed:', error);
            toast.error('Failed to upload image. Please try again.');
        } finally {
            setImageUploadLoading(false);
            if (event.target) {
                event.target.value = '';
            }
        }
    };

    const handleOpenCreateDialog = () => {
        setEditingPrice(null);
        setPriceValue('');
        setPriceCurrency(product.prices[0]?.value.currency.code || 'EUR');
        setPriceStock('');
        setPriceError(null);
        setPriceDialogOpen(true);
    };

    const handleOpenEditDialog = (price: ProductPriceDto) => {
        setEditingPrice(price);
        setPriceValue(String(price.value.value));
        setPriceCurrency(price.value.currency.code);
        setPriceStock(price.availableStock ? String(price.availableStock) : '');
        setPriceError(null);
        setPriceDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setPriceDialogOpen(false);
        setEditingPrice(null);
        setPriceValue('');
        setPriceCurrency('');
        setPriceStock('');
        setPriceError(null);
    };

    const handleSavePrice = async () => {
        const valueNum = parseFloat(priceValue);
        const stockNum = priceStock ? parseInt(priceStock) : undefined;

        if (isNaN(valueNum) || valueNum <= 0) {
            setPriceError('Please enter a valid price value');
            return;
        }

        if (priceStock && (isNaN(stockNum!) || stockNum! < 0)) {
            setPriceError('Please enter a valid stock number');
            return;
        }

        setPriceLoading(true);
        setPriceError(null);

        try {
            if (editingPrice) {
                await walletClient.supportApi.productPriceSupportControllerUpdate(
                    productId,
                    editingPrice.id,
                    {
                        value: valueNum,
                        availableStock: stockNum
                    }
                );
            } else {
                await walletClient.supportApi.productPriceSupportControllerCreate(
                    productId,
                    {
                        value: {
                            value: valueNum,
                            currencyCode: priceCurrency
                        },
                        availableStock: stockNum
                    }
                );
            }

            handleCloseDialog();
            onPriceUpdate();
        } catch (err: any) {
            console.error('Error saving price:', err);
            setPriceError(
                err.response?.data?.message ||
                    err.message ||
                    'Failed to save price'
            );
        } finally {
            setPriceLoading(false);
        }
    };

    const handleOpenDeleteDialog = (price: ProductPriceDto) => {
        setDeletingPrice(price);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setDeletingPrice(null);
    };

    const handleDeletePrice = async () => {
        if (!deletingPrice) {
            return;
        }

        setPriceLoading(true);

        try {
            await walletClient.supportApi.productPriceSupportControllerDelete(
                productId,
                deletingPrice.id
            );

            handleCloseDeleteDialog();
            onPriceUpdate();
        } catch (err: any) {
            console.error('Error deleting price:', err);
            toast.error(
                err.response?.data?.message ||
                    err.message ||
                    'Failed to delete price'
            );
        } finally {
            setPriceLoading(false);
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <FormSection
                title="Product Details"
                actions={
                    <Button
                        onClick={() =>
                            navigate(links.editProduct(vendorId, productId))
                        }
                    >
                        <Pencil />
                        Edit
                    </Button>
                }
            >
                <div className="grid gap-6 md:grid-cols-12">
                    <div className="flex flex-col gap-3 md:col-span-5">
                        {product.image && (
                            <>
                                <div
                                    className="relative w-full overflow-hidden rounded-lg border bg-muted pt-[100%]"
                                    title="Product image in 1:1 aspect ratio"
                                >
                                    <img
                                        src={getCdnImage(
                                            product.image.bucketName,
                                            product.image.objectKey,
                                            {
                                                resize: {
                                                    width: 800,
                                                    height: 800
                                                }
                                            }
                                        )}
                                        alt={`Product ${product.symbol}`}
                                        className="absolute inset-0 size-full object-cover"
                                    />
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <div className="flex-1">
                                        <ImageRequirements
                                            aspectRatio="1:1 (e.g., 800×800, 1000×1000)"
                                            minimumSize="400×400 pixels"
                                        />
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleFileChange}
                                    />
                                    <Button
                                        variant="outline"
                                        loading={imageUploadLoading}
                                        onClick={handleImageSelect}
                                    >
                                        <Upload />
                                        {imageUploadLoading
                                            ? 'Uploading...'
                                            : 'Select New Image'}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="md:col-span-7">
                        <h3 className="text-xs font-bold tracking-wide text-muted-foreground uppercase">
                            Product Information
                        </h3>
                        <div className="mt-2 grid gap-4 border-t pt-3 sm:grid-cols-2">
                            <InfoField label="ID">{product.id}</InfoField>
                            <InfoField label="Symbol">
                                {product.symbol}
                            </InfoField>
                            <InfoField label="External ID">
                                {product.externalId || '-'}
                            </InfoField>
                            {product.value && (
                                <InfoField label="Face Value">
                                    {product.value.value}{' '}
                                    {product.value.currency.code}
                                </InfoField>
                            )}
                        </div>
                    </div>
                </div>
            </FormSection>

            <FormSection title="State Information">
                <StateSummary
                    internalState={product.internalState}
                    externalState={product.externalState}
                    shopState={product.shopState}
                    loading={loading}
                    enableLabel="Enable for production"
                    onToggleState={onToggleState}
                />
            </FormSection>

            <FormSection
                title="Prices"
                actions={
                    <Button variant="outline" onClick={handleOpenCreateDialog}>
                        <Plus />
                        Add Price
                    </Button>
                }
            >
                <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                    {product.prices.map((price, index) => (
                        <div key={index} className="rounded-md border p-4">
                            {price.variant?.en && (
                                <span className="mb-1 block text-xs text-muted-foreground">
                                    Variant: {price.variant.en}
                                </span>
                            )}
                            <div className="flex items-center justify-between">
                                <span className="text-lg font-bold text-primary">
                                    {price.value.value}{' '}
                                    {price.value.currency.code}
                                </span>
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        onClick={() =>
                                            handleOpenEditDialog(price)
                                        }
                                    >
                                        <Pencil />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        className="text-destructive"
                                        onClick={() =>
                                            handleOpenDeleteDialog(price)
                                        }
                                    >
                                        <Trash2 />
                                    </Button>
                                </div>
                            </div>
                            {price.availableStock !== undefined && (
                                <span className="mt-1 block text-xs text-muted-foreground">
                                    Stock: {price.availableStock}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </FormSection>

            <Dialog
                open={priceDialogOpen}
                onOpenChange={(next) => !next && handleCloseDialog()}
            >
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingPrice ? 'Edit Price' : 'Add New Price'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col gap-4">
                        {priceError && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {priceError}
                                </AlertDescription>
                            </Alert>
                        )}
                        <TextField
                            label="Price Value"
                            type="number"
                            value={priceValue}
                            onChange={(value) => setPriceValue(String(value))}
                            required
                        />
                        <div>
                            <TextField
                                label="Currency Code"
                                type="text"
                                value={priceCurrency}
                                disabled={Boolean(editingPrice)}
                                required={!editingPrice}
                                onChange={(value) =>
                                    setPriceCurrency(
                                        String(value).toUpperCase()
                                    )
                                }
                            />
                            {!editingPrice && (
                                <p className="mt-1 text-xs text-muted-foreground">
                                    e.g., EUR, USD, GBP
                                </p>
                            )}
                        </div>
                        <div>
                            <TextField
                                label="Available Stock"
                                type="number"
                                value={priceStock}
                                onChange={(value) =>
                                    setPriceStock(String(value))
                                }
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                Optional - leave empty for unlimited
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={handleCloseDialog}
                            disabled={priceLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSavePrice}
                            loading={priceLoading}
                        >
                            {editingPrice ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onOpenChange={(next) => !next && handleCloseDeleteDialog()}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Price</DialogTitle>
                    </DialogHeader>
                    <div>
                        <p className="text-sm">
                            Are you sure you want to delete the price{' '}
                            <strong>
                                {deletingPrice?.value.value}{' '}
                                {deletingPrice?.value.currency.code}
                            </strong>
                            ?
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                            This action cannot be undone.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={handleCloseDeleteDialog}
                            disabled={priceLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeletePrice}
                            loading={priceLoading}
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
